import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const formSchema = z.object({
  email: z
    .string()
    .min(1, "El correo electrónico es requerido")
    .email("Debe ser un correo electrónico válido"),
});

type FormData = z.infer<typeof formSchema>;

export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "Correo enviado",
        description:
          "Se ha enviado un enlace para restablecer tu contraseña a tu correo electrónico.",
      });
      navigate("/login");
    } catch (error) {
      console.error("Error sending reset password email:", error);
      toast({
        title: "Error",
        description:
          "No se pudo enviar el correo de restablecimiento. Por favor, intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen">
        {/* Left Section - Features & Capabilities */}
        <div className="hidden lg:flex lg:w-3/5 bg-legal-lightblue text-legal-navy p-16 flex-col justify-center">
          <div className="max-w-lg">
            {/* Logo/Brand */}
            <div className="mb-12">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-legal-blue rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-lg">M</span>
                </div>
                <h1 className="text-3xl font-bold">
                  <span className="text-legal-navy">Mi Asistente</span>{" "}
                  <span className="text-legal-blue">Legal IA</span>
                </h1>
              </div>
              <h2 className="text-4xl font-bold mb-2">
                Restablece tu{" "}
                <span className="text-legal-blue">contraseña</span>
              </h2>
              <p className="text-legal-navy/70 text-lg">
                Te ayudamos a recuperar el acceso a tu cuenta de forma segura
              </p>
            </div>

            {/* Features List */}
            <div className="space-y-6 mb-12">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 bg-legal-blue rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-legal-navy">
                    Proceso Seguro
                  </h3>
                  <p className="text-legal-navy/70 text-sm">
                    Encriptación de extremo a extremo para proteger tu
                    información
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 bg-legal-blue rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-legal-navy">
                    Envío Rápido
                  </h3>
                  <p className="text-legal-navy/70 text-sm">
                    Recibe el enlace de restablecimiento en segundos
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 bg-legal-blue rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-legal-navy">
                    Enlace Temporal
                  </h3>
                  <p className="text-legal-navy/70 text-sm">
                    El enlace expira automáticamente por seguridad
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 bg-legal-blue rounded-full flex items-center justify-center mt-1">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-legal-navy">
                    Soporte Inmediato
                  </h3>
                  <p className="text-legal-navy/70 text-sm">
                    Si no recibes el correo, contacta a nuestro soporte
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Link */}
            <div className="mt-8">
              <p className="text-sm text-gray-500">
                ¿Necesitas ayuda adicional?{" "}
                <Link to="/#contact" className="text-blue-600 hover:underline">
                  Contactar soporte
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right Section - Forgot Password Form */}
        <div className="w-full lg:w-2/5 bg-white flex items-center justify-center p-12">
          <div className="w-full max-w-lg">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-legal-navy mb-2">
                Restablecer Contraseña
              </h1>
              <p className="text-gray-600">
                Ingresa tu correo electrónico y te enviaremos un enlace para
                restablecer tu contraseña
              </p>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Correo Electrónico
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="correo@ejemplo.com"
                          className="h-12 border-gray-300 focus:border-legal-blue focus:ring-legal-blue rounded-lg"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="pt-4">
                  <Button
                    type="submit"
                    className="w-full h-12 bg-legal-blue hover:bg-legal-navy text-white font-medium text-base rounded-lg"
                    disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Enviando correo...
                      </>
                    ) : (
                      "Enviar enlace de restablecimiento"
                    )}
                  </Button>
                </div>
              </form>
            </Form>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">
                ¿Recuerdas tu contraseña?{" "}
                <Link
                  to="/login"
                  className="text-legal-blue hover:underline font-medium">
                  Iniciar sesión
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
